<script setup lang="ts">
import { ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import debounce from "lodash-es/debounce";

import type {
  FranchiseType,
  FranchisePayloadType,
} from "@/common/types/franchise";

import { baseInput, baseTextEditor } from "@/common/components";

const { t } = useI18n();

const emit = defineEmits(["update:data", "salve:data"]);

const props = defineProps<{
  data?: FranchiseType | null;
  isPatch?: boolean;
}>();

const DEFAULT_DATA = {
  owner_id: props.data?.franchise_id,
  name: "",
  slug: "",
  description: "",
};

const payload = ref<FranchisePayloadType>({ ...DEFAULT_DATA });

function update(key: keyof typeof payload.value) {
  if (props.isPatch) {
    emit("update:data", { [key]: payload.value[key] });
    return;
  }

  emit("update:data", payload.value);
}

function resumeData() {
  if (!props.data) return;

  const { name, slug, description } = props.data;

  payload.value = {
    ...payload.value,
    name,
    slug,
    description,
  };
}

function maskCode(data: string) {
  return data?.replaceAll(" ", "-")?.toUpperCase();
}

function maskSlug(data: string) {
  return data?.replaceAll(" ", "-")?.toLowerCase();
}

const debouceUpdate = debounce(update, 300);

watchEffect(() => {
  resumeData();
});
</script>

<template>
  <section class="form-franchise">
    <base-input
      v-model="payload.name"
      :value="payload.name"
      :placeholder="t('franchise.placeholders.name')"
      required
      @update:modelValue="debouceUpdate('name')"
    >
      <template #label> {{ t("franchise.labels.name") }} * </template>
    </base-input>

    <base-input
      v-model="payload.slug"
      :value="payload.slug"
      :placeholder="t('franchise.placeholders.slug')"
      :mask="maskSlug"
      @update:modelValue="debouceUpdate('slug')"
    >
      <template #label> {{ t("franchise.labels.slug") }}</template>
    </base-input>

    <base-text-editor
      v-model="payload.description"
      @update:modelValue="debouceUpdate('description')"
    >
      <template #label> {{ t("franchise.labels.description") }}</template>
    </base-text-editor>
  </section>
</template>

<style lang="scss" scoped>
.form-franchise {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 1rem;
}
</style>
